import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RefreshCw, Download, Terminal, Settings } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOG_LEVELS = [
  { value: 'DEBUG', label: 'DEBUG', color: 'bg-purple-500', description: 'Información de depuración detallada' },
  { value: 'INFO', label: 'INFO', color: 'bg-blue-500', description: 'Información general' },
  { value: 'WARNING', label: 'WARNING', color: 'bg-yellow-500', description: 'Advertencias' },
  { value: 'ERROR', label: 'ERROR', color: 'bg-red-500', description: 'Errores' },
  { value: 'CRITICAL', label: 'CRITICAL', color: 'bg-red-900', description: 'Errores críticos' }
];

export default function AdminLogs() {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [currentLevel, setCurrentLevel] = useState('INFO');
  const [selectedLevel, setSelectedLevel] = useState('INFO');
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [lineCount, setLineCount] = useState(100);

  const token = localStorage.getItem('token');
  const config = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    // Verificar si hay token
    if (!token) {
      toast.error('Debes iniciar sesión');
      navigate('/admin');
      return;
    }
    
    loadLogLevel();
    loadLogs();
  }, [token, navigate]);

  const loadLogLevel = async () => {
    try {
      const res = await axios.get(`${API}/logs/level`, config);
      setCurrentLevel(res.data.current_level);
      setSelectedLevel(res.data.current_level);
    } catch (err) {
      console.error('Error loading log level:', err);
      if (err.response?.status === 401) {
        toast.error('Sesión expirada. Por favor, inicia sesión nuevamente');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/admin');
      } else {
        toast.error('Error al cargar nivel de logs');
      }
    }
  };

  const loadLogs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/logs/recent?lines=${lineCount}`, config);
      setLogs(res.data.logs);
    } catch (err) {
      console.error('Error loading logs:', err);
      if (err.response?.status === 401) {
        toast.error('Sesión expirada. Por favor, inicia sesión nuevamente');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/admin');
      } else {
        toast.error('Error al cargar logs');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateLevel = async () => {
    if (selectedLevel === currentLevel) {
      toast.info('El nivel ya está configurado');
      return;
    }

    setUpdating(true);
    try {
      await axios.put(`${API}/logs/level?level=${selectedLevel}`, {}, config);
      setCurrentLevel(selectedLevel);
      toast.success(`Nivel de logging cambiado a ${selectedLevel}`);
    } catch (err) {
      console.error('Error updating log level:', err);
      toast.error('Error al cambiar nivel de logging');
    } finally {
      setUpdating(false);
    }
  };

  const downloadLogs = () => {
    const blob = new Blob([logs.join('')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `logs_${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Logs descargados');
  };

  const getLogLevelColor = (logLine) => {
    if (logLine.includes('ERROR')) return 'text-red-500';
    if (logLine.includes('WARNING')) return 'text-yellow-500';
    if (logLine.includes('DEBUG')) return 'text-purple-500';
    if (logLine.includes('CRITICAL')) return 'text-red-900 font-bold';
    return 'text-gray-700';
  };

  const currentLevelInfo = LOG_LEVELS.find(l => l.value === currentLevel);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Terminal className="w-8 h-8" />
              Logs del Sistema
            </h1>
            <p className="text-muted-foreground mt-2">
              Monitorea y ajusta el nivel de trazas sin reiniciar el servidor
            </p>
          </div>
        </div>

        {/* Configuración de Nivel */}
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <Settings className="w-5 h-5" />
            <h2 className="text-xl font-semibold">Configuración de Nivel de Logs</h2>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-4 flex-wrap">
              <div>
                <label className="text-sm font-medium mb-2 block">Nivel Actual:</label>
                {currentLevelInfo && (
                  <Badge className={`${currentLevelInfo.color} text-white`}>
                    {currentLevel}
                  </Badge>
                )}
              </div>

              <div className="flex-1 min-w-[200px]">
                <label className="text-sm font-medium mb-2 block">Cambiar a:</label>
                <Select value={selectedLevel} onValueChange={setSelectedLevel}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LOG_LEVELS.map(level => (
                      <SelectItem key={level.value} value={level.value}>
                        <div className="flex items-center gap-2">
                          <span className={`w-3 h-3 rounded-full ${level.color}`} />
                          {level.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-2 items-end">
                <Button
                  onClick={handleUpdateLevel}
                  disabled={updating || selectedLevel === currentLevel}
                  className="gap-2"
                >
                  {updating ? 'Actualizando...' : 'Aplicar Cambio'}
                </Button>
              </div>
            </div>

            <div className="p-4 bg-muted rounded-lg">
              <p className="text-sm font-medium mb-2">ℹ️ Niveles de Logging:</p>
              <ul className="text-sm space-y-1">
                {LOG_LEVELS.map(level => (
                  <li key={level.value} className="flex items-start gap-2">
                    <span className={`w-3 h-3 rounded-full ${level.color} mt-1 flex-shrink-0`} />
                    <span>
                      <strong>{level.label}:</strong> {level.description}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-muted-foreground mt-3">
                💡 Tip: Usa DEBUG para diagnóstico detallado. INFO para operación normal. 
                Cambiar el nivel NO requiere reiniciar el servidor.
              </p>
            </div>
          </div>
        </Card>

        {/* Visor de Logs */}
        <Card className="p-6">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-3">
              <Terminal className="w-5 h-5" />
              <h2 className="text-xl font-semibold">Logs Recientes</h2>
              <Badge variant="outline">{logs.length} líneas</Badge>
            </div>
            
            <div className="flex gap-2">
              <Select 
                value={lineCount.toString()} 
                onValueChange={(val) => setLineCount(parseInt(val))}
              >
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="50">50 líneas</SelectItem>
                  <SelectItem value="100">100 líneas</SelectItem>
                  <SelectItem value="200">200 líneas</SelectItem>
                  <SelectItem value="500">500 líneas</SelectItem>
                  <SelectItem value="1000">1000 líneas</SelectItem>
                </SelectContent>
              </Select>

              <Button
                variant="outline"
                size="sm"
                onClick={loadLogs}
                disabled={loading}
                className="gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Recargar
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={downloadLogs}
                disabled={logs.length === 0}
                className="gap-2"
              >
                <Download className="w-4 h-4" />
                Descargar
              </Button>
            </div>
          </div>

          <div className="bg-black rounded-lg p-4 overflow-auto max-h-[600px] font-mono text-xs">
            {loading ? (
              <div className="text-gray-400 text-center py-8">
                Cargando logs...
              </div>
            ) : logs.length === 0 ? (
              <div className="text-gray-400 text-center py-8">
                No hay logs disponibles
              </div>
            ) : (
              <div className="space-y-0.5">
                {logs.map((line, index) => (
                  <div 
                    key={index} 
                    className={`${getLogLevelColor(line)} hover:bg-gray-900 px-2 py-0.5 rounded`}
                  >
                    {line}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mt-4 p-3 bg-muted rounded text-xs text-muted-foreground">
            <p>
              <strong>Ubicación:</strong> backend/logs/app_*.log | 
              <strong> Rotación:</strong> 10MB máximo por archivo | 
              <strong> Backups:</strong> 5 archivos
            </p>
          </div>
        </Card>
      </div>
    </AdminLayout>
  );
}
